import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UserOrdersService } from 'src/app/services/user-orders.service';
import { BenefitsService } from 'src/app/services/benefits.service';
import { UserDataService } from 'src/app/services/user-data.service';
import { PromoCampaignService } from 'src/app/services/promo-campaign.service';
import { OrderPdfService } from 'src/app/services/order-pdf.service';

@Component({
  selector: 'view-user-order',
  templateUrl: './view-user-order.component.html',
  styleUrls: ['./view-user-order.component.css']
})
export class ViewUserOrderComponent implements OnInit {

  public isSpinnerVisible: boolean = false;
  public order: any = null;
  public items: Array<any> = [];

  // Email użytkownika, którego zamówienie podgląda administrator (z query param).
  // null => zwykły tryb: zalogowany user ogląda własne zamówienie.
  public adminViewedEmail: string | null = null;

  public get hasOrder() {
    return this.order !== null;
  }

  // Widok wywołany przez admina z konta innego użytkownika (jest email w URL).
  public get isAdminView(): boolean {
    return !!this.adminViewedEmail;
  }

  // Belka tytułowa: dla admina "Zamówienie: <email>", inaczej "Moje zamówienie".
  public get viewTitle(): string {
    return this.isAdminView ? `Zamówienie: ${this.adminViewedEmail}` : 'Moje zamówienie';
  }

  public get campaignName() {
    return this.order?.campaignType === 'pumps' ? 'POMPY CIEPŁA' : 'KLIMATYZACJA';
  }

  // Forma nazwy kampanii do tytułu PDF i nazwy pliku.
  public get campaignNameTitle() {
    return this.order?.campaignType === 'pumps' ? 'pompy ciepła' : 'klimatyzacje';
  }

  public get formattedOrderDate() {
    return this.formatWarsaw(this.order?.orderDate);
  }

  public get orderTotal() {
    return this.order?.priceSum ?? 0;
  }

  public get parcelLocker() {
    return this.order?.inpostParcelLocker ?? null;
  }

  public get campaignActive() {
    return this.promoCampaignService.campaignActive;
  }

  constructor(
    private route: ActivatedRoute,
    private userOrdersService: UserOrdersService,
    private benefitsService: BenefitsService,
    private userDataService: UserDataService,
    private promoCampaignService: PromoCampaignService,
    private orderPdfService: OrderPdfService
  ) { }

  public async downloadPdf(): Promise<void> {
    if (!this.order) {
      return;
    }
    await this.orderPdfService.downloadOrderPdf({
      campaignName: this.campaignName,
      campaignNameTitle: this.campaignNameTitle,
      orderDate: this.formattedOrderDate,
      userEmail: this.order.userEmail ?? '',
      parcelLocker: this.parcelLocker,
      total: this.orderTotal,
      items: this.items
    });
  }

  public async ngOnInit(): Promise<void> {
    this.adminViewedEmail = this.obtainUserEmailFromQueryParameter();
    // Zwykły user widzi zakładkę tylko przy aktywnej kampanii; admin podgląda zawsze.
    if (!this.campaignActive && !this.isAdminView) {
      return;
    }
    this.isSpinnerVisible = true;
    if (this.isAdminView) {
      // Tryb admina: zamówienie wskazanego użytkownika (po emailu z URL).
      this.order = await this.userOrdersService.getCurrentCampaignOrderByEmail(this.adminViewedEmail!);
    } else {
      const currentUser = this.userDataService.currentUser;
      if (currentUser) {
        this.order = await this.userOrdersService.getCurrentCampaignOrder(currentUser);
      }
    }
    if (this.order) {
      this.items = await this.buildEnrichedItems(this.order);
    }
    this.isSpinnerVisible = false;
  }

  // Email użytkownika do podglądu przez admina przekazywany w query param `userEmail`.
  private obtainUserEmailFromQueryParameter(): string | null {
    let userEmail: string | null = null;
    this.route.queryParams.subscribe(parameters => {
      userEmail = parameters['userEmail'] ?? null;
    });
    return userEmail;
  }

  // Wzbogaca pozycje zamówienia o nazwę i grafikę benefitu z kolekcji bieżącej kampanii.
  private async buildEnrichedItems(order: any): Promise<Array<any>> {
    const benefits = await this.benefitsService.getAllExistingBenefits();
    const benefitsById = new Map<string, any>((benefits ?? []).map((b: any) => [String(b._id), b]));
    return (order.items ?? []).map((item: any) => {
      const benefit = benefitsById.get(String(item.benefitId));
      return {
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        lineTotal: item.unitPrice * item.quantity,
        name: benefit?.name ?? 'Nagroda niedostępna',
        imageUrl: benefit?.imageUrl ?? 'assets/benefit.png'
      };
    });
  }

  // Formatowanie do "data godzina" w strefie czasowej Warszawy (obsługuje CET/CEST).
  // Angularowy DatePipe nie przyjmuje nazw IANA, dlatego używamy Intl.
  private formatWarsaw(iso: string): string {
    if (!iso) {
      return '';
    }
    return new Intl.DateTimeFormat('pl-PL', {
      timeZone: 'Europe/Warsaw',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(iso));
  }
}
